import React, { useState, useEffect } from 'react';
import { apiConfig, buildApiUrl } from '../config/api';

interface LeadershipRole {
  role: string;
  assignmentCount: number;
  uniquePeople: number;
  uniqueGroups: number;
}

interface GroupLeader {
  personId: string;
  firstName: string;
  lastName: string;
  role: string;
  groupId: string;
  groupName: string;
  description?: string;
  schedule?: string;
}

interface LeadershipSummary {
  totalGroups: number;
  totalMembers: number;
  totalLeaders: number;
  avgGroupSize: number;
  leadershipRate: number;
}

interface LeadershipData {
  leadershipRoles: LeadershipRole[];
  groupLeaders: GroupLeader[];
  summary: LeadershipSummary;
}

const LeadershipAnalytics: React.FC = () => {
  const [data, setData] = useState<LeadershipData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('all');

  useEffect(() => {
    fetchLeadershipData();
  }, []);

  const fetchLeadershipData = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(apiConfig.endpoints.analytics.leadership));
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError('Failed to fetch leadership data');
      }
    } catch (err) {
      setError('Error fetching leadership data');
      console.error('Leadership data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button 
          onClick={fetchLeadershipData}
          className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return <div>No leadership data available</div>;
  }

  const filteredLeaders = selectedRole === 'all' 
    ? data.groupLeaders 
    : data.groupLeaders.filter(leader => leader.role === selectedRole);

  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      'leader': 'bg-blue-100 text-blue-800',
      'co-leader': 'bg-green-100 text-green-800',
      'host': 'bg-purple-100 text-purple-800',
      'member': 'bg-gray-100 text-gray-800',
      'default': 'bg-orange-100 text-orange-800'
    };
    return colors[role.toLowerCase()] || colors.default;
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">👑 Leadership Analytics</h1>
        <p className="text-gray-600 mb-6">
          Insights into group leadership structure, roles, and leadership development opportunities.
        </p>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-800">Total Groups</h3>
            <p className="text-2xl font-bold text-blue-900">{data.summary.totalGroups}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-green-800">Total Leaders</h3>
            <p className="text-2xl font-bold text-green-900">{data.summary.totalLeaders}</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-purple-800">Total Members</h3>
            <p className="text-2xl font-bold text-purple-900">{data.summary.totalMembers}</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-orange-800">Avg Group Size</h3>
            <p className="text-2xl font-bold text-orange-900">{data.summary.avgGroupSize}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-red-800">Leadership Rate</h3>
            <p className="text-2xl font-bold text-red-900">{data.summary.leadershipRate}%</p>
          </div>
        </div>

        {/* Leadership Roles Distribution */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">📊 Leadership Roles Distribution</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.leadershipRoles.map((role) => (
              <div
                key={role.role}
                className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{role.role}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(role.role)}`}>
                    {role.assignmentCount} assignments
                  </span>
                </div>
                <div className="space-y-1 text-sm text-gray-600">
                  <p>👥 {role.uniquePeople} unique people</p>
                  <p>🏠 {role.uniqueGroups} groups</p>
                  <p>📈 {(role.assignmentCount / role.uniquePeople).toFixed(1)} avg assignments per person</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Group Leaders Table */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">👥 Group Leaders</h2>
            <div className="flex items-center space-x-2">
              <label htmlFor="role-filter" className="text-sm font-medium text-gray-700">
                Filter by role:
              </label>
              <select
                id="role-filter"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Roles</option>
                {data.leadershipRoles.map((role) => (
                  <option key={role.role} value={role.role}>
                    {role.role} ({role.uniquePeople})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Group
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Schedule
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLeaders.map((leader, index) => (
                  <tr key={`${leader.personId}-${leader.groupId}-${index}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {leader.firstName} {leader.lastName}
                      </div>
                      <div className="text-sm text-gray-500">
                        ID: {leader.personId}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(leader.role)}`}>
                        {leader.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{leader.groupName}</div>
                      <div className="text-sm text-gray-500">Group ID: {leader.groupId}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {leader.schedule || 'Not specified'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      {leader.description || 'No description'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredLeaders.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No leaders found for the selected role.
            </div>
          )}
        </div>

        {/* Leadership Development Insights */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-green-900 mb-3">🌱 Leadership Development Insights</h2>
          <div className="space-y-2 text-sm text-green-800">
            <p>
              • <strong>Leadership Pipeline:</strong> {data.summary.totalLeaders} current leaders could mentor new group leaders
            </p>
            <p>
              • <strong>Leadership Rate:</strong> {data.summary.leadershipRate}% of group members have leadership roles
            </p>
            <p>
              • <strong>Growth Opportunity:</strong> Groups with {Math.round(data.summary.avgGroupSize)} average size could develop more co-leaders
            </p>
            <p>
              • <strong>Succession Planning:</strong> Consider identifying potential leaders in groups with single leadership
            </p>
            <p>
              • <strong>Leadership Pipeline:</strong> {data.summary.totalMembers - data.summary.totalLeaders} members could be developed into leadership roles
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadershipAnalytics;
